import React, { useEffect, useRef, useState } from "react"; // useRef is for checkbox close outside box
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { requestStubs } from "../../actions/agencyStubs";
import { fetchAgency } from "../../actions/agency";

import Globals from "../../utils/globals";
import StatsSheets from "../../components/Stats/StatsSheets";
import StatsReport from "../../components/Stats/StatsReport";
import StatsCharts from "../../components/Stats/StatsCharts";
import StatsHeatMap from "../../components/Stats/StatsHeatMap";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import "./Stats.css";

const Stats = () => {
    const dispatch = useDispatch();
    const { getAccessTokenSilently } = useAuth0();

    const agency = useSelector((state) => state.agency || {});
    const agencyStubs = useSelector(
        (state) => state.agencyStubs || {}
    );

    const agencyId = agencyStubs?.selectedAgencyId;
    const currentYear = new Date().getFullYear();

    // state declarations //
    const [period, setPeriod] = useState("month");
    const [year, setYear] = useState(currentYear);
    const [month, setMonth] = useState(new Date().getMonth() + 1);// defaults day view to current month

    // Custom date range.
    // When both dates are selected, the custom range takes precedence
    // over the Report By / Month / Year controls.
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // ---------------------------------------------------------------------
    // INCIDENT TYPE FILTER
    //
    // Options come from the agency's public/incident report schema.
    // Filtering behavior will be added after the options are verified.
    // ---------------------------------------------------------------------
    const [incidentTypeOptions, setIncidentTypeOptions] = useState([]);
    const [selectedIncidentTypes, setSelectedIncidentTypes] = useState([]);
    const [incidentTypesOpen, setIncidentTypesOpen] = useState(false);
    const incidentTypesRef = useRef(null);
    const [reportSource, setReportSource] = useState("all");

        // ---------------------------------------------------------------------
    // RESPONDER INCIDENT TYPE FILTER
    //
    // Options come from the agency's responder/incident report schema.
    //
    // This control is shown only when the agency has configured
    // responder incident-type options from its responder/incident schema.
    // An empty selectedResponderIncidentTypes array means "All".
    //
    // Filtering behavior will be connected after the UI and configured
    // responder incident choices are verified in staging.
    // ---------------------------------------------------------------------
    const [
        responderIncidentTypeOptions,
        setResponderIncidentTypeOptions,
    ] = useState([]);

    const [
        selectedResponderIncidentTypes,
        setSelectedResponderIncidentTypes,
    ] = useState([]);

    const [
        responderIncidentTypesOpen,
        setResponderIncidentTypesOpen,
    ] = useState(false);

    const responderIncidentTypesRef = useRef(null);

    // ---------------------------------------------------------------------
    // RESOLUTION TYPE MULTI-SELECT
    // Tracks selected resolution types and whether the checkbox menu is open.
    // An empty selectedResolutionTypes array means "All" resolution types.
    // ---------------------------------------------------------------------
    const [resolutionType, setResolutionType] = useState("all");
    const [selectedResolutionTypes, setSelectedResolutionTypes] = useState([]);
    const [resolutionTypesOpen, setResolutionTypesOpen] = useState(false);
    const resolutionTypesRef = useRef(null);
    const chartReportRef = useRef(null);
    const heatMapReportRef = useRef(null);
    const [resolutionTypeOptions, setResolutionTypeOptions] = useState([]);

    // True when the agency collection says it has responders
    // This is the authoritative Stats switch for responder-aware functionality.
    const [hasResponders, setHasResponders] = useState(false);

    // True when the selected agency has a state/clearance report schema.
    // This independently controls whether the Confirmed Resolution Type
    // filter is available in Stats.
    const [hasResolutionSchema, setHasResolutionSchema] = useState(false);
    const [spreadsheetData, setSpreadsheetData] = useState(null);
    // Separate data for the new incident-type statistical matrix.
    // This does not affect Ernie's Working Spreadsheet.
    const [statisticalMatrixData, setStatisticalMatrixData] =
    useState(null);
    const [chartData, setChartData] = useState(null);
    const [heatMapData, setHeatMapData] = useState(null);
    const [metricFields, setMetricFields] = useState([]);
    // Tracks which downloadable report is currently being generated.
    // Used to show "Generating Download..." and prevent duplicate clicks.
    const [generatingDownload, setGeneratingDownload] = useState(null);



    useEffect(() => {
        const ensureAgencyLoaded = async () => {
            try {
                const token = await getAccessTokenSilently();

                if (
                    !agencyStubs.selectedAgencyId &&
                    agencyStubs.items?.length === 0
                ) {
                    dispatch(requestStubs(token));
                } else if (
                    agencyStubs.selectedAgencyId &&
                    !agency?.details?._id
                ) {
                    dispatch(
                        fetchAgency(
                            agencyStubs.selectedAgencyId,
                            token
                        )
                    );
                }
            } catch (err) {
                console.error(
                    "Stats.js - Failed to load agency:",
                    err
                );
            }
        };

        ensureAgencyLoaded();
    }, [
        agencyStubs.selectedAgencyId,
        agencyStubs.items?.length,
        agency?.details?._id,
        dispatch,
        getAccessTokenSilently,
    ]);


    // ---------------------------------------------------------------------
    // LOAD PUBLIC INCIDENT TYPES
    //
    // Loads the configured incident choices from the agency's single
    // public/incident report schema through the Stats filter-options endpoint.
    // ---------------------------------------------------------------------
    useEffect(() => {
        const loadIncidentTypes = async () => {
            if (!agencyId) return;

            try {
                const token = await getAccessTokenSilently();

                const url =
                    `${Globals.apiEndpoint}/stats/agencies/${agencyId}` +
                    `/filter_options`;

                const resp = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const json = await resp.json();

                if (!resp.ok) {
                    console.error(
                        "[Stats] failed loading Incident Types:",
                        json
                    );
                    return;
                }

                const options = Array.isArray(
                    json?.incident_types
                )
                    ? json.incident_types
                    : [];

                                const responderOptions = Array.isArray(
                    json?.responder_incident_types
                )
                    ? json.responder_incident_types
                    : [];

                const responderEnabled =
                    json?.has_responders === true;

                setIncidentTypeOptions(options);
                setResponderIncidentTypeOptions(
                    responderOptions
                );
                setHasResponders(responderEnabled);

                // Reset responder selections when changing agencies so
                // selections from one agency cannot carry into another.
                setSelectedResponderIncidentTypes([]);
                setResponderIncidentTypesOpen(false);

                                console.log(
                    "[Stats] Responder-enabled agency:",
                    responderEnabled
                );

                console.log(
                    "[Stats] Responder Incident Type Options:",
                    responderOptions
                );

                console.log(
                    "[Stats] Incident Type Options:",
                    options
                );

            } catch (error) {
                console.error(
                    "[Stats] failed loading Incident Types:",
                    error
                );
            }
        };

        loadIncidentTypes();
    }, [agencyId, getAccessTokenSilently]);

    useEffect(() => {
        const loadResolutionTypes = async () => {
            if (!agencyId) return;

            try {
                const token = await getAccessTokenSilently();

                const url =
                    `${Globals.apiEndpoint}/resolutions/agencies/${agencyId}` +
                    `/clearance_schema`;

                const resp = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const json = await resp.json();

                if (!resp.ok) {
                    setHasResolutionSchema(false);
                    setResolutionTypeOptions([]);
                    setMetricFields([]);

                    console.error(
                        "[Stats] failed loading Resolution Form:",
                        json
                    );
                    return;
                }

                setHasResolutionSchema(
                    Boolean(json?.resolution_schema)
                );

                const items =
                    json?.resolution_schema?.items || [];

                const metrics = items.filter(
                    (item) =>
                        (item?.type === "number" || item?.type === "integer") &&
                        item?.should_be_aggregated === true
                );

                setMetricFields(metrics);
                const resolutionTypeField = items.find(
                    (item) =>
                        item?.type === "list" &&
                        item?.statistic_name === "Resolution Type"
                );

                const options = Array.isArray(
                    resolutionTypeField?.items
                )
                    ? resolutionTypeField.items
                    : [];

                setResolutionTypeOptions(options);

            } catch (error) {
                console.error(
                    "[Stats] failed loading Resolution Types:",
                    error
                );
            }
        };

        loadResolutionTypes();
    }, [agencyId, getAccessTokenSilently]);

    // ---------------------------------------------------------------------
    // CLOSE STATS MULTI-SELECT MENUS WHEN USER CLICKS OUTSIDE
    //
    // Keeps checkbox selections intact; only closes the dropdown panels
    // for Public Incident Type, Responder Incident Type and Resolution Type.
    // ---------------------------------------------------------------------
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                resolutionTypesRef.current &&
                !resolutionTypesRef.current.contains(event.target)
            ) {
                setResolutionTypesOpen(false);
            }

            if (
                incidentTypesRef.current &&
                !incidentTypesRef.current.contains(event.target)
            ) {
                setIncidentTypesOpen(false);
            }

            if (
                responderIncidentTypesRef.current &&
                !responderIncidentTypesRef.current.contains(
                    event.target
                )
            ) {
                setResponderIncidentTypesOpen(false);
            }
        };

        document.addEventListener(
            "mousedown",
            handleClickOutside
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };
    }, []);


    // Run Report Function //
    const runReport = async () => {
        if (!agencyId) return;

        setChartData(null);
        setHeatMapData(null);
        setStatisticalMatrixData(null);

        try {
            const token = await getAccessTokenSilently();
            const resolutionTypeQuery =
                selectedResolutionTypes.length === 0
                    ? "all"
                    : selectedResolutionTypes.join("|");

            const incidentTypeQuery =
                selectedIncidentTypes.length === 0
                    ? "all"
                    : selectedIncidentTypes.join("|");

            const responderIncidentTypeQuery =
                selectedResponderIncidentTypes.length === 0
                    ? "all"
                    : selectedResponderIncidentTypes.join("|");

            const url =
                `${Globals.apiEndpoint}/stats/agencies/${agencyId}` +
                `/spreadsheet` +
                `?period=${period}` +
                `&year=${year}` +
                `&month=${month}` +
                `&source=${reportSource}` +
                `&incident_type=${encodeURIComponent(incidentTypeQuery)}` +
                `&responder_incident_type=${encodeURIComponent(responderIncidentTypeQuery)}` +
                `&resolution_type=${encodeURIComponent(resolutionTypeQuery)}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await response.json();

            console.log(
                "[Spreadsheet Endpoint]",
                JSON.stringify(json, null, 2)
            );
            setSpreadsheetData(json);
        } catch (err) {
            console.error(
                "[Stats] spreadsheet request failed:",
                err
            );
        }
    };

        // ---------------------------------------------------------------------
    // RUN STATISTICAL MATRIX REPORT
    //
    // Powers ONLY the blue "View Spreadsheet" button in the Spreadsheet
    // result card.
    //
    // This is intentionally separate from runReport(), which continues
    // powering Ernie's existing "View Working Spreadsheet".
    //
    // Backend:
    //   GET /stats/agencies/:aid/statistical_matrix
    // ---------------------------------------------------------------------
    const runStatisticalMatrixReport = async () => {
        if (!agencyId) return;

        setChartData(null);
        setHeatMapData(null);
        setSpreadsheetData(null);
        setStatisticalMatrixData(null);

        try {
            const token = await getAccessTokenSilently();

            const resolutionTypeQuery =
                selectedResolutionTypes.length === 0
                    ? "all"
                    : selectedResolutionTypes.join("|");

            const incidentTypeQuery =
                selectedIncidentTypes.length === 0
                    ? "all"
                    : selectedIncidentTypes.join("|");

            const responderIncidentTypeQuery =
                selectedResponderIncidentTypes.length === 0
                    ? "all"
                    : selectedResponderIncidentTypes.join("|");

            const url =
                `${Globals.apiEndpoint}/stats/agencies/${agencyId}` +
                `/statistical_matrix` +
                `?period=${period}` +
                `&year=${year}` +
                `&month=${month}` +
                `&source=${reportSource}` +
                `&incident_type=${encodeURIComponent(incidentTypeQuery)}` +
                `&responder_incident_type=${encodeURIComponent(responderIncidentTypeQuery)}` +
                `&resolution_type=${encodeURIComponent(resolutionTypeQuery)}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await response.json();

            if (!response.ok) {
                throw new Error(
                    json?.message ||
                    `Statistical matrix failed: ${response.status}`
                );
            }

            console.log(
                "[Statistical Matrix Endpoint]",
                JSON.stringify(json, null, 2)
            );

            setStatisticalMatrixData(json);

        } catch (err) {
            console.error(
                "[Stats] statistical matrix request failed:",
                err
            );
        }
    };

    // ---------------------------------------------------------------------
    // RUN CHART REPORT
    //
    // Loads visual statistics using the same agency/date/source/resolution
    // filters as the spreadsheet report.
    //
    // Backend:
    //   GET /stats/agencies/:aid/chart
    //
    // Returns:
    // - Incident Types — As Reported
    // - Incident Types — Final / Effective
    // - Locations — As Reported
    // - GPS points for the incident heat map
    // ---------------------------------------------------------------------
    const runChartReport = async () => {
        if (!agencyId) return;

        setSpreadsheetData(null);
        setHeatMapData(null);
        setStatisticalMatrixData(null);

        try {
            const token = await getAccessTokenSilently();

            const resolutionTypeQuery =
                selectedResolutionTypes.length === 0
                    ? "all"
                    : selectedResolutionTypes.join("|");

            const incidentTypeQuery =
                selectedIncidentTypes.length === 0
                    ? "all"
                    : selectedIncidentTypes.join("|");

            const responderIncidentTypeQuery =
                selectedResponderIncidentTypes.length === 0
                    ? "all"
                    : selectedResponderIncidentTypes.join("|");

            const url =
                `${Globals.apiEndpoint}/stats/agencies/${agencyId}` +
                   `/statistical_matrix` +
                `?period=${period}` +
                `&year=${year}` +
                `&month=${month}` +
                `&source=${reportSource}` +
                `&incident_type=${encodeURIComponent(incidentTypeQuery)}` +
                `&responder_incident_type=${encodeURIComponent(responderIncidentTypeQuery)}` +
                `&resolution_type=${encodeURIComponent(resolutionTypeQuery)}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await response.json();

            if (!response.ok) {
                throw new Error(
                    json?.message ||
                    `Chart report failed: ${response.status}`
                );
            }

            console.log(
                "[Chart Endpoint]",
                JSON.stringify(json, null, 2)
            );

            setChartData(json);

        } catch (err) {
            console.error(
                "[Stats] chart request failed:",
                err
            );
        }
    };


    // ---------------------------------------------------------------------
    // RUN HEAT MAP
    //
    // Uses the same Stats /chart endpoint and filters as the Chart Report,
    // but stores only the Heat Map result separately.
    //
    // This keeps the Heat Map independent from the bar-chart report.
    // ---------------------------------------------------------------------
    const runHeatMap = async () => {
        if (!agencyId) return;

        setSpreadsheetData(null);
        setChartData(null);
        setStatisticalMatrixData(null);

        try {
            const token = await getAccessTokenSilently();

            const resolutionTypeQuery =
                selectedResolutionTypes.length === 0
                    ? "all"
                    : selectedResolutionTypes.join("|");

            const incidentTypeQuery =
                selectedIncidentTypes.length === 0
                    ? "all"
                    : selectedIncidentTypes.join("|");
            const responderIncidentTypeQuery =
                selectedResponderIncidentTypes.length === 0
                    ? "all"
                    : selectedResponderIncidentTypes.join("|");

            const url =
                `${Globals.apiEndpoint}/stats/agencies/${agencyId}` +
                `/chart` +
                `?period=${period}` +
                `&year=${year}` +
                `&month=${month}` +
                `&source=${reportSource}` +
                `&incident_type=${encodeURIComponent(incidentTypeQuery)}` +
                `&responder_incident_type=${encodeURIComponent(responderIncidentTypeQuery)}` +
                `&resolution_type=${encodeURIComponent(resolutionTypeQuery)}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const json = await response.json();

            if (!response.ok) {
                throw new Error(
                    json?.message ||
                    `Heat Map report failed: ${response.status}`
                );
            }

            console.log(
                "[Heat Map Endpoint]",
                JSON.stringify(
                    json?.heatmap_points || [],
                    null,
                    2
                )
            );

            setHeatMapData(json);

        } catch (err) {
            console.error(
                "[Stats] Heat Map request failed:",
                err
            );
        }
    };
    // ---------------------------------------------------------------------
    // DOWNLOAD STATS EXCEL WORKBOOK
    // Builds the Excel request using the same filters as the browser report.
    // Multiple Resolution Types are sent as a pipe-delimited string.
    // No selected types means "All".
    // ---------------------------------------------------------------------
    const downloadExcel = async () => {
        if (!agencyId) return;

        setGeneratingDownload("excel");

        try {
            const token = await getAccessTokenSilently();

            const resolutionTypeQuery =
                selectedResolutionTypes.length === 0
                    ? "all"
                    : selectedResolutionTypes.join("|");

            const incidentTypeQuery =
                selectedIncidentTypes.length === 0
                    ? "all"
                    : selectedIncidentTypes.join("|");

            const url =
                `${Globals.apiEndpoint}/stats/agencies/${agencyId}/excel` +
                `?period=${period}` +
                `&year=${year}` +
                `&month=${month}` +
                `&source=${reportSource}` +
                `&incident_type=${encodeURIComponent(incidentTypeQuery)}` +
                `&resolution_type=${encodeURIComponent(resolutionTypeQuery)}`;
            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(
                    `Excel download failed: ${response.status}`
                );
            }

            const blob = await response.blob();

            const downloadUrl =
                window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download = "transit-watch-statistics.xlsx";

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error(
                "[Stats] Excel download failed:",
                err
            );

        } finally {
            setGeneratingDownload(null);
        }
    };

    // ---------------------------------------------------------------------
    // DOWNLOAD FILTERED SPREADSHEET
    //
    // Downloads the same filtered/grouped data shown by View Spreadsheet.
    //
    // Backend:
    //   GET /stats/agencies/:aid/spreadsheet/download
    //
    // This is separate from downloadExcel(), which downloads the
    // comprehensive Annual Statistics Workbook.
    // ---------------------------------------------------------------------
    const downloadSpreadsheet = async () => {
        if (!agencyId) return;

        setGeneratingDownload("spreadsheet");

        try {
            const token = await getAccessTokenSilently();

            const resolutionTypeQuery =
                selectedResolutionTypes.length === 0
                    ? "all"
                    : selectedResolutionTypes.join("|");

            const incidentTypeQuery =
                selectedIncidentTypes.length === 0
                    ? "all"
                    : selectedIncidentTypes.join("|");

            const responderIncidentTypeQuery =
                selectedResponderIncidentTypes.length === 0
                    ? "all"
                    : selectedResponderIncidentTypes.join("|");

            const url =
                `${Globals.apiEndpoint}/stats/agencies/${agencyId}` +
                `/spreadsheet/download` +
                `?period=${period}` +
                `&year=${year}` +
                `&month=${month}` +
                `&source=${reportSource}` +
                `&incident_type=${encodeURIComponent(incidentTypeQuery)}` +
                `&responder_incident_type=${encodeURIComponent(responderIncidentTypeQuery)}` +
                `&resolution_type=${encodeURIComponent(resolutionTypeQuery)}`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(
                    `Spreadsheet download failed: ${response.status}`
                );
            }

            const blob = await response.blob();

            const downloadUrl =
                window.URL.createObjectURL(blob);

            const link = document.createElement("a");

            link.href = downloadUrl;
            link.download =
                "transit-watch-statistics-report.xlsx";

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error(
                "[Stats] Spreadsheet download failed:",
                err
            );
        } finally {
            setGeneratingDownload(null);
        }
    };

    // ---------------------------------------------------------------------
    // DOWNLOAD CHART REPORT PDF
    // Creates a client-facing PDF from the currently rendered chart report.
    // ---------------------------------------------------------------------
    const downloadChartPdf = async () => {
        if (!chartData || !chartReportRef.current) return;

        setGeneratingDownload("chartPdf");

        try {
            const agencyName =
                agency?.details?.name?.en_US || "Transit Watch";

            const monthNames = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
            ];

            let reportingPeriod = String(year);

            if (period === "day") {
                reportingPeriod =
                    `${monthNames[month - 1]} ${year}`;
            } else if (period === "month") {
                reportingPeriod = String(year);
            } else if (period === "quarter") {
                reportingPeriod = String(year);
            }
                        const groupingLabel =
                period === "day"
                    ? "Daily"
                    : period === "month"
                    ? "Monthly"
                    : period === "quarter"
                    ? "Quarterly"
                    : "Annual";

            const resolutionLabel =
                selectedResolutionTypes.length === 0
                    ? "All"
                    : selectedResolutionTypes.join(", ");

            const sourceLabel =
                reportSource === "all"
                    ? "All"
                    : reportSource.charAt(0).toUpperCase() +
                    reportSource.slice(1);

            // -------------------------------------------------------------
            // LOAD BSG LOGO
            // -------------------------------------------------------------
            const logo = new Image();

            logo.src = "/BSG-logo.png";

            await new Promise((resolve, reject) => {
                logo.onload = resolve;
                logo.onerror = reject;
            });

            const logoCanvas =
                document.createElement("canvas");

            logoCanvas.width =
                logo.naturalWidth || logo.width;

            logoCanvas.height =
                logo.naturalHeight || logo.height;

            const logoContext =
                logoCanvas.getContext("2d");

            logoContext.drawImage(
                logo,
                0,
                0,
                logoCanvas.width,
                logoCanvas.height
            );

            const logoData =
                logoCanvas.toDataURL("image/png");

            // -------------------------------------------------------------
            // FIND ALL CURRENTLY RENDERED INCIDENT STATISTICS CHARTS
            //
            // StatsCharts renders one chart for each reporting period and
            // incident family. Public charts are always included when data
            // exists; Responder charts appear only for responder-enabled
            // agencies with matching data.
            //
            // Preserve the exact browser display order in the PDF.
            // -------------------------------------------------------------
            const chartElements = Array.from(
                chartReportRef.current.querySelectorAll(
                    "[data-pdf-chart]"
                )
            ).map((element, index) => ({
                id:
                    element.getAttribute(
                        "data-pdf-chart"
                    ) || `chart-${index + 1}`,
                element,
            }));


            if (chartElements.length === 0) {
                throw new Error(
                    "No chart elements were found for the PDF."
                );
            }

            // -------------------------------------------------------------
            // CREATE PDF
            // -------------------------------------------------------------
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "pt",
                format: "letter",
            });

            const pageWidth =
                pdf.internal.pageSize.getWidth();

            const pageHeight =
                pdf.internal.pageSize.getHeight();

            const margin = 36;

            const contentWidth =
                pageWidth - margin * 2;

            // -------------------------------------------------------------
            // REUSABLE PAGE HEADER
            // -------------------------------------------------------------
            const drawHeader = (pageNumber) => {
                const logoWidth = 95;

                const logoHeight =
                    (logoCanvas.height * logoWidth) /
                    logoCanvas.width;

                // Center the BSG logo at the top of every report page.
                const logoX =
                    (pageWidth - logoWidth) / 2;

                pdf.addImage(
                    logoData,
                    "PNG",
                    logoX,
                    20,
                    logoWidth,
                    logoHeight
                );

                pdf.setTextColor(31, 45, 69);

                pdf.setFont(
                    "helvetica",
                    "bold"
                );

                pdf.setFontSize(17);

                // agencyName already contains "Transit Watch" where applicable.
                pdf.text(
                    agencyName,
                    pageWidth / 2,
                    82,
                    { align: "center" }
                );

                pdf.setFontSize(14);

                pdf.text(
                    "Statistics Report",
                    pageWidth / 2,
                    102,
                    { align: "center" }
                );

                pdf.setFont(
                    "helvetica",
                    "normal"
                );

                pdf.setFontSize(9);
                pdf.setTextColor(75);

                                pdf.text(
                    `Reporting Period: ${reportingPeriod}`,
                    pageWidth / 2,
                    120,
                    { align: "center" }
                );

                pdf.text(
                    `Report Grouping: ${groupingLabel}`,
                    pageWidth / 2,
                    135,
                    { align: "center" }
                );

                pdf.text(
                    `Report Source: ${sourceLabel}`,
                    pageWidth / 2,
                    150,
                    { align: "center" }
                );

                pdf.setDrawColor(
                    31,
                    45,
                    69
                );

                pdf.setLineWidth(1.2);

                pdf.line(
                    margin,
                    165,
                    pageWidth - margin,
                    165
                );

                // Footer
                pdf.setFont(
                    "helvetica",
                    "normal"
                );

                pdf.setFontSize(8);
                pdf.setTextColor(110);

                pdf.text(
                    `© ${new Date().getFullYear()} The Brass Star Group, LLC. All rights reserved.`,
                    margin,
                    pageHeight - 18
                );

                pdf.text(
                    `Page ${pageNumber}`,
                    pageWidth - margin,
                    pageHeight - 18,
                    { align: "right" }
                );

                pdf.setTextColor(0);
            };

            // -------------------------------------------------------------
            // BUILD ONE CHART PER PAGE
            // -------------------------------------------------------------
            for (
                let index = 0;
                index < chartElements.length;
                index += 1
            ) {
                if (index > 0) {
                    pdf.addPage();
                }

                const pageNumber = index + 1;

                drawHeader(pageNumber);

                // Total incident count appears on page 1.
                let chartTop = 185;

                if (index === 0) {
                    pdf.setFont(
                        "helvetica",
                        "bold"
                    );

                    pdf.setFontSize(11);

                    pdf.setTextColor(
                        31,
                        45,
                        69
                    );

                    pdf.text(
                        `Total Incidents: ${chartData.total_incidents || 0}`,
                        margin,
                        187
                    );

                    pdf.setTextColor(0);

                    chartTop = 203;
                }

                const canvas =
                    await html2canvas(
                        chartElements[index].element,
                        {
                            scale: 2,
                            backgroundColor: "#ffffff",
                            useCORS: true,
                        }
                    );

                const maxChartHeight =
                    pageHeight -
                    chartTop -
                    48;

                const naturalHeight =
                    (canvas.height * contentWidth) /
                    canvas.width;

                let imageWidth =
                    contentWidth;

                let imageHeight =
                    naturalHeight;

                // Scale down only when necessary so the complete chart
                // remains on one page and is never split.
                if (imageHeight > maxChartHeight) {
                    const scale =
                        maxChartHeight /
                        imageHeight;

                    imageHeight =
                        maxChartHeight;

                    imageWidth =
                        contentWidth * scale;
                }

                const imageX =
                    (pageWidth - imageWidth) / 2;

                const imageData =
                    canvas.toDataURL(
                        "image/png"
                    );

                pdf.addImage(
                    imageData,
                    "PNG",
                    imageX,
                    chartTop,
                    imageWidth,
                    imageHeight
                );
            }

            // -------------------------------------------------------------
            // SAVE PDF
            // -------------------------------------------------------------
            const safeAgencyName =
                agencyName
                    .replace(
                        /[^a-z0-9]+/gi,
                        "-"
                    )
                    .replace(
                        /^-+|-+$/g,
                        ""
                    );

            pdf.save(
                `${safeAgencyName}-Transit-Watch-Statistics-${year}.pdf`
            );

        } catch (error) {
            console.error(
                "[Stats] PDF download failed:",
                error
            );
        } finally {
            setGeneratingDownload(null);
        }
    };


    // ---------------------------------------------------------------------
    // DOWNLOAD HEAT MAP PDF
    // Creates a branded landscape PDF from the currently rendered heat map.
    // ---------------------------------------------------------------------
        const downloadHeatMapPdf = async () => {
        if (!heatMapData || !heatMapReportRef.current) return;

        setGeneratingDownload("heatMapPdf");

        try {
            const agencyName =
                agency?.details?.name?.en_US || "Transit Watch";

            const monthNames = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
            ];

            let reportingPeriod = String(year);

            if (period === "day") {
                reportingPeriod =
                    `Daily — ${monthNames[month - 1]} ${year}`;
            } else if (period === "month") {
                reportingPeriod =
                    `Monthly — ${year}`;
            } else if (period === "quarter") {
                reportingPeriod =
                    `Quarterly — ${year}`;
            } else if (period === "year") {
                reportingPeriod =
                    `Annual — ${year}`;
            }

            const resolutionLabel =
                selectedResolutionTypes.length === 0
                    ? "All"
                    : selectedResolutionTypes.join(", ");

            const sourceLabel =
                reportSource === "all"
                    ? "All"
                    : reportSource.charAt(0).toUpperCase() +
                      reportSource.slice(1);

            // -------------------------------------------------------------
            // LOAD BSG LOGO
            // -------------------------------------------------------------
            const logo = new Image();

            logo.src = "/BSG-logo.png";

            await new Promise((resolve, reject) => {
                logo.onload = resolve;
                logo.onerror = reject;
            });

            const logoCanvas =
                document.createElement("canvas");

            logoCanvas.width =
                logo.naturalWidth || logo.width;

            logoCanvas.height =
                logo.naturalHeight || logo.height;

            const logoContext =
                logoCanvas.getContext("2d");

            logoContext.drawImage(
                logo,
                0,
                0,
                logoCanvas.width,
                logoCanvas.height
            );

            const logoData =
                logoCanvas.toDataURL("image/png");

            // -------------------------------------------------------------
            // FIND ALL CURRENTLY RENDERED HEAT MAP CARDS
            // -------------------------------------------------------------
            const mapElements = Array.from(
                heatMapReportRef.current.querySelectorAll(
                    "[data-pdf-heatmap]"
                )
            );

            if (mapElements.length === 0) {
                throw new Error(
                    "No Heat Map elements were found for the PDF."
                );
            }

            // -------------------------------------------------------------
            // CREATE LANDSCAPE PDF
            // -------------------------------------------------------------
            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "pt",
                format: "letter",
            });

            const pageWidth =
                pdf.internal.pageSize.getWidth();

            const pageHeight =
                pdf.internal.pageSize.getHeight();

            const margin = 36;

            // -------------------------------------------------------------
            // REUSABLE PAGE HEADER / FOOTER
            // -------------------------------------------------------------
            const drawPageFrame = (pageNumber) => {
                const logoWidth = 95;

                const logoHeight =
                    (logoCanvas.height * logoWidth) /
                    logoCanvas.width;

                const logoX =
                    (pageWidth - logoWidth) / 2;

                pdf.addImage(
                    logoData,
                    "PNG",
                    logoX,
                    18,
                    logoWidth,
                    logoHeight
                );

                pdf.setTextColor(31, 45, 69);

                pdf.setFont(
                    "helvetica",
                    "bold"
                );

                pdf.setFontSize(17);

                pdf.text(
                    agencyName,
                    pageWidth / 2,
                    76,
                    { align: "center" }
                );

                pdf.setFontSize(14);

                pdf.text(
                    "Incident Location Heat Map",
                    pageWidth / 2,
                    96,
                    { align: "center" }
                );

                pdf.setFont(
                    "helvetica",
                    "normal"
                );

                pdf.setFontSize(9);
                pdf.setTextColor(75);

                pdf.text(
                    `Reporting Period: ${reportingPeriod}`,
                    pageWidth / 2,
                    114,
                    { align: "center" }
                );

                pdf.text(
                    `Report Source: ${sourceLabel}   •   Resolution Type: ${resolutionLabel}`,
                    pageWidth / 2,
                    129,
                    { align: "center" }
                );

                pdf.setDrawColor(
                    31,
                    45,
                    69
                );

                pdf.setLineWidth(1.2);

                pdf.line(
                    margin,
                    144,
                    pageWidth - margin,
                    144
                );

                pdf.setFont(
                    "helvetica",
                    "normal"
                );

                pdf.setFontSize(8);
                pdf.setTextColor(110);

                pdf.text(
                    `© ${new Date().getFullYear()} The Brass Star Group, LLC. All rights reserved.`,
                    margin,
                    pageHeight - 18
                );

                pdf.text(
                    `Page ${pageNumber}`,
                    pageWidth - margin,
                    pageHeight - 18,
                    { align: "right" }
                );

                pdf.setTextColor(0);
            };

            // -------------------------------------------------------------
            // ONE RENDERED HEAT MAP PER PDF PAGE
            // -------------------------------------------------------------
            for (
                let index = 0;
                index < mapElements.length;
                index += 1
            ) {
                if (index > 0) {
                    pdf.addPage();
                }

                drawPageFrame(index + 1);

                const canvas =
                    await html2canvas(
                        mapElements[index],
                        {
                            scale: 2,
                            backgroundColor: "#ffffff",
                            useCORS: true,
                            logging: false,
                        }
                    );

                const imageData =
                    canvas.toDataURL("image/png");

                const imageTop = 156;
                const footerSpace = 32;

                const maxImageWidth =
                    pageWidth - margin * 2;

                const maxImageHeight =
                    pageHeight -
                    imageTop -
                    footerSpace;

                const widthScale =
                    maxImageWidth / canvas.width;

                const heightScale =
                    maxImageHeight / canvas.height;

                const scale =
                    Math.min(
                        widthScale,
                        heightScale
                    );

                const renderedWidth =
                    canvas.width * scale;

                const renderedHeight =
                    canvas.height * scale;

                const imageX =
                    (pageWidth - renderedWidth) / 2;

                pdf.addImage(
                    imageData,
                    "PNG",
                    imageX,
                    imageTop,
                    renderedWidth,
                    renderedHeight
                );
            }

            // -------------------------------------------------------------
            // FILE NAME
            // -------------------------------------------------------------
            const safeAgencyName =
                agencyName
                    .replace(/[^a-z0-9]+/gi, "-")
                    .replace(/^-+|-+$/g, "");

            pdf.save(
                `${safeAgencyName}-Transit-Watch-Heat-Map-${year}.pdf`
            );

        } catch (error) {
            console.error(
                "[Stats] Heat Map PDF download failed:",
                error
            );
        } finally {
            setGeneratingDownload(null);
        }
    };

    // end temp ex file test

    // this is JSX code below //
    return (
        <div className="container-fluid stats-page">
            <div className="stats-title-row">
                <h3>
                    {agency?.details?.name?.en_US
                        ? `${agency.details.name.en_US} Statistics`
                        : "Statistics"}
                </h3>

                <div className="stats-subtitle">
                    Security Metrics
                </div>
            </div>
           <div className="stats-workflow">

    {/* ---------------------------------------------------------
        STEP 1 - REPORT SOURCE
        --------------------------------------------------------- */}
    <div className="stats-step stats-step-source">
        <div className="row align-items-center g-4">

            {/* STEP DESCRIPTION */}
            <div className="col-lg-3">
                <div className="stats-step-heading mb-0">
                    <div className="stats-step-number">
                        1
                    </div>

                    <div>
                        <h4 className="stats-step-title">
                            Choose the Report Source
                        </h4>

                        <div className="stats-step-help">
                            Select where the incident reports
                            originated from.
                        </div>
                    </div>
                </div>
            </div>

            {/* REPORT SOURCE CONTROL */}
            <div className="col-lg-5">
                <label className="stats-filter-label">
                    Report Source
                </label>

                <select
                    className="form-select"
                    value={reportSource}
                    onChange={(e) =>
                        setReportSource(e.target.value)
                    }
                >
                    <option value="all">All</option>
                    <option value="public">Public</option>
                    <option value="securitas">
                        Securitas
                    </option>
                </select>
            </div>

            {/* REPORT SOURCE HELP */}
            <div className="col-lg-4">
                <div className="stats-info-box">
                    <div className="stats-info-icon">
                        i
                    </div>

                    <div>
                        <div className="stats-info-title">
                            Report Source
                        </div>

                        <div className="stats-info-text">
                            Choose the source of the reports
                            you want to analyze.
                        </div>
                    </div>
                </div>
            </div>

        </div> {/* END Step 1 row */}
    </div> {/* END Step 1 */}

    {/* ---------------------------------------------------------
    STEP 2 - REPORTING PERIOD
    --------------------------------------------------------- */}
<div className="stats-step stats-step-period">
    <div className="row align-items-center g-4">

        {/* STEP DESCRIPTION */}
        <div className="col-lg-3">
            <div className="stats-step-heading mb-0">
                <div className="stats-step-number stats-step-number-period">
                    2
                </div>

                <div>
                    <h4 className="stats-step-title">
                        Choose How to Group the Results
                    </h4>

                    <div className="stats-step-help">
                        View results by days, months, quarters, or the full year.
                    </div>
                </div>
            </div>
        </div>

        {/* REPORTING PERIOD CONTROLS */}
        <div className="col-lg-5">
            <div className="row g-2">

                {/* REPORT BY */}
                <div className="col stats-period-report-by">
                    <label className="stats-filter-label">
                        Group Results By
                    </label>

                    <select
                        className="form-select"
                        value={period}
                        onChange={(e) =>
                            setPeriod(e.target.value)
                        }
                    >
                        <option value="day">Days in Month</option>
                        <option value="month">Months in Year</option>
                        <option value="quarter">Quarters in Year</option>
                        <option value="year">Full Year</option>
                    </select>
                </div>

                {/* MONTH - ONLY SHOW WHEN REPORTING BY DAY */}
                {period === "day" && (
                   <div className="col stats-period-month">
                        <label className="stats-filter-label">
                            Month
                        </label>

                        <select
                            className="form-select"
                            value={month}
                            onChange={(e) =>
                                setMonth(Number(e.target.value))
                            }
                        >
                            <option value={1}>January</option>
                            <option value={2}>February</option>
                            <option value={3}>March</option>
                            <option value={4}>April</option>
                            <option value={5}>May</option>
                            <option value={6}>June</option>
                            <option value={7}>July</option>
                            <option value={8}>August</option>
                            <option value={9}>September</option>
                            <option value={10}>October</option>
                            <option value={11}>November</option>
                            <option value={12}>December</option>
                        </select>
                    </div>
                )}

                {/* YEAR */}
                <div className="col stats-period-year">
                    <label className="stats-filter-label">
                        Year
                    </label>

                    <select
                        className="form-select"
                        value={year}
                        onChange={(e) =>
                            setYear(Number(e.target.value))
                        }
                    >
                        <option value={currentYear - 2}>
                            {currentYear - 2}
                        </option>

                        <option value={currentYear - 1}>
                            {currentYear - 1}
                        </option>

                        <option value={currentYear}>
                            {currentYear}
                        </option>
                    </select>
                </div>

            </div> {/* END reporting period controls row */}
        </div> {/* END reporting period controls */}

        {/* CUSTOM DATE RANGE */}
        <div className="col-lg-4">
            <div className="stats-date-range-placeholder">
                <div className="stats-info-title">
                    Custom Date Range
                </div>

                <div className="row g-2 align-items-end">
                    <div className="col-md-6">
                        <label className="stats-date-label">
                            Start Date
                        </label>

                        <input
                            type="date"
                            className="form-control"
                            value={startDate}
                            disabled
                            onChange={(e) =>
                                setStartDate(e.target.value)
                            }
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="stats-date-label">
                            End Date
                        </label>

                        <input
                            type="date"
                            className="form-control"
                            value={endDate}
                            disabled
                            onChange={(e) =>
                                setEndDate(e.target.value)
                            }
                        />
                    </div>
                </div>

                <div className="stats-date-range-help">
                    Custom Date Range — Coming Soon
                </div>
            </div>
        </div>

    </div> {/* END Step 2 row */}
</div> {/* END Step 2 */}

{/* ---------------------------------------------------------
    STEP 3 - INCIDENT TYPES
    --------------------------------------------------------- */}
<div className="stats-step stats-step-incidents">
    <div className="row align-items-center g-4">

        {/* STEP DESCRIPTION */}
        <div className="col-lg-4">
            <div className="stats-step-heading mb-0">
                <div className="stats-step-number stats-step-number-incidents">
                    3
                </div>

                <div>
                    <h4 className="stats-step-title">
                        Choose Incident Types (Optional)
                    </h4>

                    <div className="stats-step-help">
                        Narrow the results by the type of incident
                        reported or the type of resolution.
                    </div>
                </div>
            </div>
        </div>

        {/* PUBLIC REPORTED INCIDENT TYPE */}
        <div className="col-lg-4">
            <div className="stats-filter-label">
                Public Reported Incident Type
            </div>

            <div className="stats-filter-description">
                What was originally reported.
            </div>

            <div
    ref={incidentTypesRef}
    style={{
        position: "relative",
        width: "100%",
    }}
>
    <button
        type="button"
        className="form-select"
        onClick={() =>
            setIncidentTypesOpen(
                (current) => !current
            )
        }
        style={{
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        }}
    >
        {selectedIncidentTypes.length === 0
            ? "All"
            : selectedIncidentTypes.length === 1
                ? (
                    incidentTypeOptions.find(
                        (option) =>
                            String(option?._id) ===
                            String(
                                selectedIncidentTypes[0]
                            )
                    )?.title?.en_US ||
                    "1 selected"
                )
                : `${selectedIncidentTypes.length} selected`}
    </button>

    {incidentTypesOpen && (
        <div
            style={{
                position: "absolute",
                top: "100%",
                left: 0,
                zIndex: 1000,
                width: "100%",
                minWidth: "320px",
                maxHeight: "320px",
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: "6px",
                boxShadow:
                    "0 4px 12px rgba(0,0,0,0.15)",
                padding: "8px",
                marginTop: "4px",
            }}
        >
            <label
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 4px",
                    cursor: "pointer",
                    fontWeight: 600,
                }}
            >
                <input
                    type="checkbox"
                    checked={
                        selectedIncidentTypes.length === 0
                    }
                    onChange={() => {
                        setSelectedIncidentTypes([]);
                    }}
                />

                All
            </label>

            {incidentTypeOptions.map((option) => {
                const optionId =
                    String(option?._id || "");

                const optionTitle =
                    option?.title?.en_US ||
                    "Untitled option";

                const checked =
                    selectedIncidentTypes.includes(
                        optionId
                    );

                return (
                    <label
                        key={optionId || optionTitle}
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            padding: "6px 4px",
                            cursor: "pointer",
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                                setSelectedIncidentTypes(
                                    (current) => {
                                        if (
                                            current.includes(
                                                optionId
                                            )
                                        ) {
                                            return current.filter(
                                                (value) =>
                                                    value !==
                                                    optionId
                                            );
                                        }

                                        return [
                                            ...current,
                                            optionId,
                                        ];
                                    }
                                );
                            }}
                        />

                        <span
                            style={{
                                whiteSpace: "normal",
                                lineHeight: "1.2",
                            }}
                        >
                            {optionTitle}
                        </span>
                    </label>
                );
            })}
              </div>
    )} {/* END Public Incident Type dropdown menu */}
</div>
{/* END Public Incident Type dropdown */}

        {/* ---------------------------------------------------------
            RESPONDER INCIDENT TYPE

            Shown only when responder incident-type options exist
            for the agency's responder/incident report schema.

            Options come from the agency's responder/incident
            report schema.

            An empty selection means "All".

            NOTE:
            This staging step adds and verifies the UI only.
            The responder incident-type selection is not yet sent
            to the Stats backend report generators.
            --------------------------------------------------------- */}
              {responderIncidentTypeOptions.length > 0 && (
            <div
                style={{
                    marginTop: "24px",
                }}
            >
                <div className="stats-filter-label">
                    Responder Incident Type
                </div>
                <div className="stats-filter-description">
                    What the responder reported.
                </div>

                <div
                    ref={responderIncidentTypesRef}
                    style={{
                        position: "relative",
                        width: "100%",
                    }}
                >
                    <button
                        type="button"
                        className="form-select"
                        onClick={() =>
                            setResponderIncidentTypesOpen(
                                (current) => !current
                            )
                        }
                        style={{
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {selectedResponderIncidentTypes.length === 0
                            ? "All"
                            : selectedResponderIncidentTypes.length === 1
                                ? responderIncidentTypeOptions.find(
                                    (option) =>
                                        String(option?._id) ===
                                        String(
                                            selectedResponderIncidentTypes[0]
                                        )
                                )?.title?.en_US || "1 selected"
                                : `${selectedResponderIncidentTypes.length} selected`}
                    </button>

                    {responderIncidentTypesOpen && (
                        <div
                            style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1000,
                                width: "100%",
                                minWidth: "320px",
                                maxHeight: "320px",
                                overflowY: "auto",
                                background: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "6px",
                                boxShadow:
                                    "0 4px 12px rgba(0,0,0,0.15)",
                                padding: "8px",
                                marginTop: "4px",
                            }}
                        >
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "6px 4px",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        selectedResponderIncidentTypes.length ===
                                        0
                                    }
                                    onChange={() => {
                                        setSelectedResponderIncidentTypes(
                                            []
                                        );
                                    }}
                                />

                                All
                            </label>

                            {responderIncidentTypeOptions.map(
                                (option) => {
                                    const optionId =
                                        String(option?._id || "");

                                    const optionTitle =
                                        option?.title?.en_US ||
                                        "Untitled option";

                                    const checked =
                                        selectedResponderIncidentTypes.includes(
                                            optionId
                                        );

                                    return (
                                        <label
                                            key={
                                                optionId ||
                                                optionTitle
                                            }
                                            style={{
                                                display: "flex",
                                                alignItems:
                                                    "flex-start",
                                                gap: "8px",
                                                padding:
                                                    "6px 4px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    checked
                                                }
                                                onChange={() => {
                                                    setSelectedResponderIncidentTypes(
                                                        (
                                                            current
                                                        ) => {
                                                            if (
                                                                current.includes(
                                                                    optionId
                                                                )
                                                            ) {
                                                                return current.filter(
                                                                    (
                                                                        value
                                                                    ) =>
                                                                        value !==
                                                                        optionId
                                                                );
                                                            }

                                                            return [
                                                                ...current,
                                                                optionId,
                                                            ];
                                                        }
                                                    );
                                                }}
                                            />

                                            <span
                                                style={{
                                                    whiteSpace:
                                                        "normal",
                                                    lineHeight:
                                                        "1.2",
                                                }}
                                            >
                                                {
                                                    optionTitle
                                                }
                                            </span>
                                        </label>
                                    );
                                }
                            )}
                        </div> 
                       
                    )}  {/* END Responder Incident Type dropdown menu */}
                </div> 
                {/* END Responder Incident Type dropdown */}
            </div> 
           
        )} {/* END Responder Incident Type column */}
          </div> {/* END Public / Responder Incident Type column */}

        {/* CONFIRMED RESOLUTION TYPE / RESPONDER INFORMATION */}
        <div className="col-lg-4">

          {hasResolutionSchema ? (
                <>
                    {/* RESPONDER AGENCY - CONFIRMED RESOLUTION FILTER */}
                    <div className="stats-filter-label">
                        Confirmed Resolution Type
                    </div>

                    <div className="stats-filter-description">
                        What Security determined.
                    </div>

                    <div
                        ref={resolutionTypesRef}
                        style={{
                            position: "relative",
                            width: "100%",
                        }}
                    >
                        <button
                            type="button"
                            className="form-select"
                            onClick={() =>
                                setResolutionTypesOpen(
                                    (current) => !current
                                )
                            }
                            style={{
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {selectedResolutionTypes.length === 0
                                ? "All"
                                : selectedResolutionTypes.length === 1
                                    ? selectedResolutionTypes[0]
                                    : `${selectedResolutionTypes.length} selected`}
                        </button>

                        {resolutionTypesOpen && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    zIndex: 1000,
                                    width: "100%",
                                    minWidth: "320px",
                                    maxHeight: "320px",
                                    overflowY: "auto",
                                    background: "#fff",
                                    border: "1px solid #ccc",
                                    borderRadius: "6px",
                                    boxShadow:
                                        "0 4px 12px rgba(0,0,0,0.15)",
                                    padding: "8px",
                                    marginTop: "4px",
                                }}
                            >
                                <label
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "6px 4px",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={
                                            selectedResolutionTypes.length === 0
                                        }
                                        onChange={() => {
                                            setSelectedResolutionTypes([]);
                                        }}
                                    />

                                    All
                                </label>

                                {resolutionTypeOptions.map((option) => {
                                    const optionTitle =
                                        option?.title?.en_US ||
                                        "Untitled option";

                                    const checked =
                                        selectedResolutionTypes.includes(
                                            optionTitle
                                        );

                                    return (
                                        <label
                                            key={option?._id || optionTitle}
                                            style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: "8px",
                                                padding: "6px 4px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => {
                                                    setSelectedResolutionTypes(
                                                        (current) => {
                                                            if (
                                                                current.includes(
                                                                    optionTitle
                                                                )
                                                            ) {
                                                                return current.filter(
                                                                    (value) =>
                                                                        value !==
                                                                        optionTitle
                                                                );
                                                            }

                                                            return [
                                                                ...current,
                                                                optionTitle,
                                                            ];
                                                        }
                                                    );
                                                }}
                                            />

                                            <span
                                                style={{
                                                    whiteSpace: "normal",
                                                    lineHeight: "1.2",
                                                }}
                                            >
                                                {optionTitle}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div> {/* END resolution type dropdown */}

                </>
            ) : (
                <>
                    {/* NON-RESPONDER AGENCY - RESOLUTION INFORMATION */}
                    <div className="stats-filter-label">
                        Confirmed Resolution Types
                    </div>

                    <div className="stats-filter-description">
                        Add responder departments to compare what was
                        originally reported with what responders
                        ultimately determined.
                    </div>
                </>
            )}

        </div> {/* END Confirmed Resolution Type / Responder Information */}

    </div> {/* END Step 3 row */}
</div> {/* END Step 3 */}

{/* ---------------------------------------------------------
    STEP 4 - RESULTS / REPORT ACTIONS
    --------------------------------------------------------- */}
<div className="stats-step stats-step-results">
    <div className="row align-items-stretch g-4">

        {/* STEP DESCRIPTION */}
        <div className="col-lg-4">
            <div className="stats-step-heading mb-0">
                <div className="stats-step-number stats-step-number-results">
                    4
                </div>

                <div>
                    <h4 className="stats-step-title">
                        Choose What to Do with Your Results
                    </h4>

                    <div className="stats-step-help">
                        View your filtered results as a spreadsheet,
                        charts, or a geographic heat map.
                        You can also download the results for sharing
                        or further analysis.
                    </div>
                    {hasResponders && (
                    <div className="stats-annual-workbook">
                    <div className="stats-annual-workbook-title">
                        Annual Statistics Workbook
                    </div>

                    <div className="stats-annual-workbook-description">
                        Comprehensive statistics for the selected year.
                    </div>
                    <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={runReport}
                    >
                            View Working Spreadsheet
                    </button>

                    <button
                        type="button"
                        className="btn btn-success w-100"
                        disabled={generatingDownload === "excel"}
                        onClick={downloadExcel}
                    >
                        {generatingDownload === "excel"
                            ? "Generating Download..."
                            : "Download Annual Workbook"}
                    </button>
                    {/* END stats-annual-workbook */}
                    </div>
                )}
                </div> {/* END Step 4 heading/content */}
            </div> {/* END Step 4 description column */}
        </div>

        {/* RESULT ACTION CARDS */}
        <div className="col-lg-8">
            <div className="row g-3 h-100">

                {/* SPREADSHEET */}
                <div className="col-lg-4">
                    <div className="stats-result-card">
                        <div className="stats-result-title">
                            Spreadsheet
                        </div>

                        <div className="stats-result-description">
                            View a table of incident data and
                            unresolved incidents.
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary w-100"
                           onClick={runStatisticalMatrixReport}
                        >
                            View Spreadsheet
                        </button>

                        <button
                            type="button"
                            className="btn btn-success w-100"
                            disabled={
                               !statisticalMatrixData ||
                                generatingDownload === "spreadsheet"
                            }
                            onClick={downloadSpreadsheet}
                        >
                            {generatingDownload === "spreadsheet"
                                ? "Generating Download..."
                                : "Download Spreadsheet"}
                        </button>
                    </div>
                </div>

                {/* CHARTS */}
                <div className="col-lg-4">
                    <div className="stats-result-card">
                        <div className="stats-result-title">
                            Charts
                        </div>

                        <div className="stats-result-description">
                            View charts and graphs of incident
                            types and trends.
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={runChartReport}
                        >
                            View Charts
                        </button>

                        <button
                            type="button"
                            className="btn btn-danger w-100"
                            disabled={
                                !chartData ||
                                generatingDownload === "chartPdf"
                            }
                            onClick={downloadChartPdf}
                        >
                            {generatingDownload === "chartPdf"
                                ? "Generating Download..."
                                : "Download PDF"}
                        </button>
                    </div>
                </div>

                {/* HEAT MAP */}
                <div className="col-lg-4">
                    <div className="stats-result-card">
                        <div className="stats-result-title">
                            Heat Map
                        </div>

                        <div className="stats-result-description">
                            View the GPS locations of matching
                            incidents on a map.
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={runHeatMap}
                        >
                            View Heat Map
                        </button>

                        <button
                            type="button"
                            className="btn btn-danger w-100"
                            disabled={
                                !heatMapData ||
                                generatingDownload === "heatMapPdf"
                            }
                            onClick={downloadHeatMapPdf}
                        >
                            {generatingDownload === "heatMapPdf"
                                ? "Generating Download..."
                                : "Download PDF"}
                        </button>
                    </div>
                </div>

            </div> {/* END result action cards row */}
        </div> {/* END result action cards column */}

    </div> {/* END Step 4 row */}
</div> {/* END Step 4 */}

</div> {/* END stats-workflow */}

            {chartData && (
                <div ref={chartReportRef}>
                    <StatsCharts
                        chartData={chartData}
                    />
                </div>
            )}

            {heatMapData && (
                <div ref={heatMapReportRef}>
                    <StatsHeatMap
                        points={heatMapData.heatmap_points}
                        groups={heatMapData.heatmap_groups}
                        period={period}
                    />
                </div>
            )}

            {spreadsheetData && (
                <StatsReport
                    resolutionTypeOptions={resolutionTypeOptions}
                    metricFields={metricFields}
                    spreadsheetData={spreadsheetData}
                />
            )}
            {statisticalMatrixData && (
                 <StatsSheets
                    statisticalMatrixData={statisticalMatrixData}
                />
            )}
        </div>
    );
};

export default Stats;